// istanbul ignore file
import { datadogRum } from '@datadog/browser-rum';
import React, { useCallback, useContext, useEffect } from 'react';

import { PUBLISH_PATH, STORE_ANALYTICS_PATH } from 'src/modules/api';

interface DatadogContext {
  setDatadogUser: (id: string) => void;
}

const ctx = React.createContext<DatadogContext>({
  setDatadogUser: () => {},
});

interface DatadogProps {
  children: JSX.Element;
  environment: string;
  version: string;
}

export const DatadogProvider = ({
  children,
  environment,
  version,
}: DatadogProps) => {
  useEffect(() => {
    if (environment !== 'local') {
      // https://docs.datadoghq.com/real_user_monitoring/browser/#configuration
      datadogRum.init({
        applicationId: '199930b6-a117-43e5-83de-0e5886581076',
        clientToken: 'pubd7743a200988978e1bdff766d476c193',
        site: 'datadoghq.com',
        service: 'apply-with-seek',
        env: environment,
        version,

        // https://docs.datadoghq.com/real_user_monitoring/browser/monitoring_page_performance/#how-page-activity-is-calculated
        excludedActivityUrls: [
          new RegExp(`${PUBLISH_PATH}$`),
          new RegExp(`${STORE_ANALYTICS_PATH}$`),
        ],

        // Only sample 1% of all users to minimise session usage
        sessionSampleRate: 1,
        // Of the 1% of sampled sessions, enable session replay
        sessionReplaySampleRate: 100,
        silentMultipleInit: true,
        defaultPrivacyLevel: 'mask',
        beforeSend: (event) => {
          // Don't care about errors from browser extensions
          if (
            event.type === 'error' &&
            event.error.stack?.includes('chrome-extension://')
          ) {
            return false;
          }

          return true;
        },
      });
    }
  }, [environment, version]);

  const setDatadogUser = useCallback(
    (id: string) => {
      if (environment !== 'local') {
        datadogRum.setUser({ id });
      }
    },
    [environment],
  );

  return <ctx.Provider value={{ setDatadogUser }}>{children}</ctx.Provider>;
};

export const useDatadog = () => useContext(ctx);
